import { warn } from '@execonline-inc/logging';
import { InvalidUrlError, URLParser, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import { handleError } from '../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { SearchParameters } from '../../../../ListFilter/Types';
import ReactionComponent, { RCProps } from '../../../../ReactionComponent';
import { Link } from '../../../../Resource/Types';
import { groupParticipantsResourceDecoder } from './Decoders';
import GroupStore from './Store';
import { GroupParticipantsResource, LoadingMore, State } from './Types';

interface Props extends RCProps<GroupStore> {}

type GroupError = AppyError | InvalidUrlError | MissingLinkError;

const handleParticipantsError = (store: GroupStore) => (error: GroupError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warn('ParticipantsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const sendRequest = callApi(groupParticipantsResourceDecoder, {});

const updateSearchParams = (searchParameters: SearchParameters) => (url: URLParser) =>
  searchParameters.reduce(
    (acc, parameter) =>
      putQueryParam(`participants_filter[${parameter.key}]`, parameter.value)(acc),
    url,
  );

const buildUrl = (
  store: GroupStore,
  link: Link,
  id: number,
  page: number,
): Task<GroupError, Link> => {
  return toUrlT(link.href)
    .map(updateSearchParams(store.searchParameters))
    .map(putQueryParam('page', String(page)))
    .map((url): Link => ({ ...link, href: url.href.replace('{group_id}', `${id}`) }));
};

const getParticipants = (store: GroupStore, id: number, page: number) => (link: Link) =>
  buildUrl(store, link, id, page).andThen(sendRequest);

const concatParticipants =
  (state: LoadingMore) =>
  (newResource: GroupParticipantsResource): GroupParticipantsResource => ({
    ...newResource,
    payload: {
      ...newResource.payload,
      allParticipantIds: [
        ...state.resource.payload.allParticipantIds,
        ...newResource.payload.allParticipantIds,
      ],
      participants: {
        ...newResource.payload.participants,
        payload: {
          participants: state.resource.payload.participants.payload.participants.concat(
            newResource.payload.participants.payload.participants,
          ),
        },
      },
    },
  });

const loadMore = (store: GroupStore, state: LoadingMore) =>
  Task.succeed<GroupError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('user-group'))
    .andThen(getParticipants(store, state.id, state.page))
    .map(concatParticipants(state))
    .fork(handleParticipantsError(store), store.ready);

class GroupParticipantsReactions extends ReactionComponent<GroupStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
      case 'updating-search-parameters':
        Task.succeed<GroupError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('user-group'))
          .andThen(getParticipants(store, state.id, state.page))
          .fork(handleParticipantsError(store), store.ready);
        break;
      case 'updating-query':
        store.ready(state.resource);
        break;
      case 'sorting':
        store.loading();
        break;
      case 'ready':
      case 'error':
        break;
      case 'loading-more':
        loadMore(store, state);
        break;
      default:
        assertNever(state);
    }
  };
}

export default GroupParticipantsReactions;
